import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import { messageBus, useOperationsStore } from '@operations/AppProvider'
import { useTranslate } from '@tolgee/react'
import { EmptyIndicator, useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { VesselKPIUIStore } from '../stores/vessel-kpi.ui-store'
import { VesselKPI } from './molecules/VesselKPI'
import { VesselKPIDate } from './molecules/VesselKPIDate'

interface Props {
  visitId: number
  onViewClick?: () => void
}
export const VesselKPISummary = observer(({ visitId, onViewClick }: Props) => {
  const { t } = useTranslate()
  const { appStore, isCraneSplitOutdatedUIStore } = useOperationsStore()
  const theme = useMinimalsTheme()

  const store = useMemo(() => new VesselKPIUIStore(messageBus, appStore), [appStore])

  useAsyncFetch(
    async () => {
      if (visitId) {
        await store.getVisitPerformance(visitId)
        store.setVesselVisitId(visitId)
        await isCraneSplitOutdatedUIStore.load(visitId)
      }
    },
    [isCraneSplitOutdatedUIStore, store, visitId],
    {
      cleanUp: () => {
        isCraneSplitOutdatedUIStore.setVesselVisitId(undefined)
      },
    },
  )

  return (
    <Card sx={{ width: '100%', height: '100%' }}>
      <CardHeader
        action={
          !!onViewClick && (
            <Button color='inherit' onClick={onViewClick} data-cy='vessel-kpi-view-btn'>
              {t('view', 'View')}
            </Button>
          )
        }
        title={<Typography variant='h2'>{t('vesselKPI', 'Vessel KPI')}</Typography>}
      />

      <CardContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <VesselKPI
              title={t('averageGrossProductivity', 'Average gross productivity')}
              label={t('movesPerHour', 'Moves per hour')}
            >
              <Typography variant='h3'>
                {store.visitPerformance?.performance
                  ? Math.round(store.visitPerformance?.performance)
                  : EmptyIndicator}
              </Typography>
            </VesselKPI>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <VesselKPI
              title={t('estimatedRemainingHours', 'Estimated remaining hours')}
              label={t('hours', 'Hours')}
            >
              <Typography variant='h3'>{store.hoursLeft ?? EmptyIndicator}</Typography>
            </VesselKPI>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <VesselKPI title={t('firstMove', 'First move')}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant='subtitle2' color='secondary'>
                    {t('discharge', 'Discharge')}
                  </Typography>

                  <VesselKPIDate date={store.visitPerformance?.firstInboundDate} />
                </Grid>

                <Grid size={6}>
                  <Typography variant='subtitle2' color='secondary'>
                    {t('load', 'Load')}
                  </Typography>
                  <VesselKPIDate date={store.visitPerformance?.firstOutboundDate} />
                </Grid>
              </Grid>
            </VesselKPI>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <VesselKPI title={t('estimatedFinishTime', 'Estimated finish time')}>
              {store.timeOfCompletion ? (
                <Stack>
                  <VesselKPIDate date={store.timeOfCompletion} />
                  {isCraneSplitOutdatedUIStore.isCraneSplitOutDated && (
                    <Alert severity='warning' sx={{ mt: theme.customSpacing.s }}>
                      {t(
                        'estimatedFinishTimeIsOutdatedNewOrdersHaveBeenAdded',
                        'The estimated finish time is outdated. New orders have been added.',
                      )}
                    </Alert>
                  )}
                </Stack>
              ) : (
                <Typography variant='h3'>{EmptyIndicator}</Typography>
              )}
            </VesselKPI>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
})
